/**
 * Divergências Bling↔local — **item a item** (Task 11, brief 3.2).
 *
 * O que esta tela NÃO é: um selo "integração conectada". Cada linha é um
 * produto e um CAMPO, com o valor dos dois lados, a autoridade do campo, o
 * link para o produto e o link para o evento de sync que originou o estado.
 *
 * Três estados distintos, nunca confundidos:
 * - fonte do operacional ausente → não há comparação possível (não é "tudo
 *   sincronizado");
 * - comparação feita e nenhuma pendência → dito explicitamente;
 * - pendências → tabela item a item.
 *
 * `action_required` é a fila de trabalho (campo cuja referência é o Bling está
 * defasado no local). `expected` é diferença por desenho do catálogo em duas
 * camadas (nome comercial/descrição/SEO são da Plataforma) e aparece para
 * transparência, separada da fila.
 *
 * Migração para `.tsx` (Cluster B — Catálogo & Estoque, redesign por perfil):
 * mesmo markup/contrato de antes, só a sintaxe `createElement` → JSX. Import
 * de `React` explícito — ver nota equivalente em `layout/AppShell.tsx`.
 */
import React, { type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { GitCompare, Info, type LucideIcon, TriangleAlert, Unlink } from "lucide-react";
import { PageHeader, StatTile } from "../ui/index.js";
import { RecordTable, type RecordTableColumn } from "../components/RecordTable.js";
import { CatalogBoundary } from "../components/CatalogBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { AuthorityBadge } from "../components/AuthorityBadge.js";
import {
  formatDivergenceValue,
  summarizeDivergences,
  type CatalogDivergence,
  type DivergenceSeverity
} from "../../../../packages/catalog/src/index.js";
import { blingSourceLabel, catalogDivergencesOf, type CatalogDataset } from "../catalog/dataset.js";
import { formatDateTime } from "../format.js";
import { useReprocessDivergence } from "../queries.js";
import type { DivergenciasViewProps } from "../catalog/view-props.js";

const SEVERITY_LABELS: Record<DivergenceSeverity, string> = {
  action_required: "Ação necessária (referência Bling defasada no local)",
  expected: "Esperada (campo de autoridade da Plataforma)",
  link_missing: "Vínculo ausente entre os dois lados"
};

const SEVERITY_META: Record<DivergenceSeverity, { tone: string; Icon: LucideIcon }> = {
  // Fila de trabalho = alerta (danger). Vínculo ausente = atenção (warning).
  // Esperada por desenho = neutro (não é pendência).
  action_required: { tone: "border-danger text-danger", Icon: TriangleAlert },
  link_missing: { tone: "border-warning text-warning", Icon: Unlink },
  expected: { tone: "border-border-strong text-body", Icon: Info }
};

/**
 * Selo de severidade da divergência: ícone + texto, nunca só cor (régua §2 /
 * WCAG 1.4.1). `data-divergence-severity` e o rótulo exato seguem no wrapper —
 * hooks dos testes preservados.
 */
function SeverityBadge({ severity }: { severity: DivergenceSeverity }): ReactElement {
  const meta = SEVERITY_META[severity];
  return (
    <span
      className={"inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " + meta.tone}
      data-divergence-severity={severity}
    >
      <meta.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />
      <span>{SEVERITY_LABELS[severity]}</span>
    </span>
  );
}

const KIND_LABELS: Record<CatalogDivergence["kind"], string> = {
  value_mismatch: "Valor diferente",
  missing_local: "Existe no Bling, não existe na Plataforma",
  missing_bling: "Existe na Plataforma, sem par no Bling"
};

const SEVERITIES: readonly DivergenceSeverity[] = ["action_required", "expected", "link_missing"];

/** Lê `?severidade=`; valor desconhecido vira `null` (lista completa). */
export function parseSeverity(raw: string | null | undefined): DivergenceSeverity | null {
  if (!raw) return null;
  return (SEVERITIES as readonly string[]).includes(raw) ? (raw as DivergenceSeverity) : null;
}

/**
 * Tabela item a item. Exportada porque a MESMA tabela é usada pela tela de
 * divergências, pela tela de produto (só as linhas dele) e pela tela de
 * sincronização — um formato só, sem recálculo.
 */
export interface DivergenciasTableProps {
  divergences: CatalogDivergence[];
  /** Ação de reprocessamento (Task 17/R3); ausente = coluna de ação oculta. */
  onReprocess?: (divergence: { entityType: string; entityId: string }) => void;
  reprocessingId?: string | null;
  /** Sem moldura própria (uso dentro de um cartão, ex.: aba do produto). */
  bare?: boolean;
  /** Visão do produto: tira as colunas que repetem o mesmo valor em toda linha (SKU) ou são de apoio (data, evento de sync). */
  compact?: boolean;
}

export function DivergenciasTable({ divergences, onReprocess, reprocessingId = null, bare = false, compact = false }: DivergenciasTableProps): ReactElement {
  return (
    <RecordTable<CatalogDivergence>
      bare={bare}
      caption="Divergências entre o operacional (Bling) e a Plataforma, campo a campo"
      rows={divergences}
      idOf={(divergence) => divergence.id}
      emptyMessage="Nenhuma divergência neste recorte."
      // Fase 8 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
      animateRows
      columns={([
        {
          key: "sku",
          label: "SKU",
          render: (divergence) =>
            divergence.productId ? (
              <Link to={`/catalogo/${divergence.productId}`} className="font-semibold text-brand-text hover:underline">
                {divergence.sku}
              </Link>
            ) : (
              divergence.sku
            )
        },
        {
          key: "field",
          label: "Campo",
          render: (divergence) => (
            <span data-divergence-field={divergence.fieldKey}>
              {divergence.fieldLabel} <AuthorityBadge fieldKey={divergence.fieldKey} />
            </span>
          )
        },
        {
          key: "bling",
          label: "Valor no Bling",
          wrap: true,
          render: (divergence) => <span data-side="bling">{formatDivergenceValue(divergence.blingValue)}</span>
        },
        {
          key: "platform",
          label: "Valor na Plataforma",
          wrap: true,
          render: (divergence) => <span data-side="platform">{formatDivergenceValue(divergence.platformValue)}</span>
        },
        { key: "kind", label: "Tipo", render: (divergence) => KIND_LABELS[divergence.kind] },
        {
          key: "severity",
          label: "Situação",
          render: (divergence) => <SeverityBadge severity={divergence.severity} />
        },
        { key: "detectedAt", label: "Detectada em", render: (divergence) => formatDateTime(divergence.detectedAt) },
        {
          key: "syncLog",
          label: "Evento de sync",
          render: (divergence) =>
            divergence.syncLogId ? (
              <Link
                to={`/sincronizacao?syncLog=${encodeURIComponent(divergence.syncLogId)}`}
                className="font-semibold text-brand-text hover:underline"
              >
                {divergence.syncLogId}
              </Link>
            ) : (
              "sem evento de sync registrado"
            )
        },
        // Coluna de ação só existe quando há handler (Task 17/R3). Só divergência
        // de fila de trabalho (`action_required`) com produto vinculado é
        // reprocessável — reprocessar uma diferença ESPERADA (autoria da
        // Plataforma) não faria sentido, e sem produto local não há o que re-syncar.
        ...(onReprocess
          ? [
              {
                key: "reprocess",
                label: "Ação",
                render: (divergence: CatalogDivergence) =>
                  divergence.severity === "action_required" && divergence.productId ? (
                    <button
                      type="button"
                      className={
                        "inline-flex items-center rounded-control border border-brand bg-brand-wash px-2.5 py-1 text-xs font-semibold text-brand-text " +
                        "hover:bg-brand hover:text-on-brand focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                      }
                      data-reprocess={divergence.id}
                      data-reprocess-entity={divergence.productId}
                      disabled={reprocessingId === divergence.id}
                      onClick={() => onReprocess({ entityType: "product", entityId: divergence.productId as string })}
                    >
                      {reprocessingId === divergence.id ? "Reprocessando…" : "Reprocessar sync"}
                    </button>
                  ) : (
                    <span className="text-muted" data-reprocess-unavailable={divergence.id}>
                      —
                    </span>
                  )
              }
            ]
          : [])
      ] as RecordTableColumn<CatalogDivergence>[]).filter((column) => !compact || !["sku", "detectedAt", "syncLog"].includes(column.key))}
    />
  );
}

export function DivergenciasView({ dataset, severity = null, onReprocess, reprocessingId = null }: DivergenciasViewProps): ReactElement {
  const divergences = catalogDivergencesOf(dataset);
  if (divergences === null) {
    return (
      <div className="page page--divergencias flex flex-col gap-6" data-page="divergencias">
        <PageHeader title="Divergências Bling↔local" />
        <StatusPanel
          kind="awaiting_source"
          title="Sem o lado operacional não há comparação"
          detail="A rota /api/catalog/bling/snapshot não respondeu neste ambiente. Sem o snapshot do Bling não é possível comparar campo a campo — e dizer 'tudo sincronizado' aqui seria mentira."
        />
      </div>
    );
  }
  const summary = summarizeDivergences(divergences);
  const filtered = severity ? divergences.filter((divergence) => divergence.severity === severity) : divergences;
  const tabs: Array<{ id: "all" | DivergenceSeverity; label: string; to: string; active: boolean }> = [
    { id: "all", label: `Todas (${summary.total})`, to: "/divergencias", active: severity === null },
    { id: "action_required", label: `Ação necessária (${summary.actionRequired})`, to: "/divergencias?severidade=action_required", active: severity === "action_required" },
    { id: "link_missing", label: `Vínculo (${summary.linkMissing})`, to: "/divergencias?severidade=link_missing", active: severity === "link_missing" },
    { id: "expected", label: `Esperadas (${summary.expected})`, to: "/divergencias?severidade=expected", active: severity === "expected" }
  ];
  return (
    <div className="page page--divergencias flex flex-col gap-6" data-page="divergencias">
      <PageHeader
        title="Divergências Bling↔local"
        description="Comparação campo a campo entre o ERP (Bling) e o catálogo da Plataforma."
      />
      <section aria-label="Resumo das divergências" className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Divergências" value={String(summary.total)} icon={GitCompare} data-divergence-total={summary.total} />
        <StatTile label="Ação necessária" value={String(summary.actionRequired)} hint="Referência Bling defasada no local." icon={TriangleAlert} />
        <StatTile label="Vínculo ausente" value={String(summary.linkMissing)} hint="Item sem par no outro lado." icon={Unlink} />
        <StatTile label="Esperadas" value={String(summary.expected)} hint="Campos de autoridade da Plataforma." icon={Info} />
      </section>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-panel border border-border bg-surface p-3 shadow-s1">
        <nav className="page__filters flex flex-wrap items-center gap-2 text-sm" aria-label="Filtrar divergências por situação">
          {/* `data-filter` no wrapper: as props de `Link` não aceitam atributos
              arbitrários no tipo do react-router. */}
          {tabs.map((tab) => (
            <span key={tab.id} data-filter={tab.id}>
              <Link
                to={tab.to}
                aria-current={tab.active ? "page" : undefined}
                className={
                  "inline-flex min-h-[36px] items-center rounded-pill border px-3 text-sm font-semibold " +
                  (tab.active
                    ? "border-brand bg-brand-wash text-brand-text"
                    : "border-border-strong bg-surface text-body hover:bg-surface-soft")
                }
              >
                {tab.label}
              </Link>
            </span>
          ))}
        </nav>
        <p className="m-0 text-xs text-muted" data-bling-source={dataset.snapshot?.source ?? "absent"}>
          {blingSourceLabel(dataset.snapshot)}
        </p>
      </div>
      <p className="m-0 rounded-panel border border-warning bg-warning-soft px-4 py-3 text-sm text-ink" data-divergence-note="derived">
        <strong className="font-bold">Como ler:</strong> a fila persistida (tabela catalog_divergences, migration 0030) é escrita pelo sync do Bling na
        Task 17. Hoje estas linhas são derivadas da comparação ao vivo, com o mesmo contrato, e por isso aparecem todas como abertas.
      </p>
      <DivergenciasTable divergences={filtered} onReprocess={onReprocess} reprocessingId={reprocessingId} />
    </div>
  );
}

export function Divergencias(): ReactElement {
  const [searchParams] = useSearchParams();
  const severity = parseSeverity(searchParams.get("severidade"));
  const reprocess = useReprocessDivergence();
  // Id em voo para desabilitar só o botão clicado (react-query expõe as
  // variáveis da mutação em andamento). A verdade volta do servidor na próxima
  // leitura do catálogo (invalidada no sucesso), não de um estado local.
  const reprocessingId =
    reprocess.isPending && reprocess.variables
      ? `div_${reprocess.variables.entityId.replace(/[^a-zA-Z0-9]+/g, "_").toLowerCase()}`
      : null;
  return (
    <CatalogBoundary>
      {(dataset: CatalogDataset) => (
        <DivergenciasView
          dataset={dataset}
          severity={severity}
          onReprocess={({ entityType, entityId }) => reprocess.mutate({ entityType, entityId })}
          reprocessingId={reprocessingId}
        />
      )}
    </CatalogBoundary>
  );
}
